import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Trash2, ZoomIn, ZoomOut, Calendar, Clock, AlertTriangle, CheckCircle, Play, Pause } from 'lucide-react';
import Button from './Button';
import { ProductionTask, SampleTask } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';
import { addDays, subDays, differenceInDays, isSameDay, startOfWeek, endOfWeek, eachDayOfInterval, format } from 'date-fns';

interface GanttViewProps {
  tasks: (ProductionTask | SampleTask)[];
  onEditTask: (task: ProductionTask | SampleTask) => void;
  onDeleteTask: (id: string) => void;
  onUpdateTaskDates: (taskId: string, startDate: string, endDate: string) => void;
  getSupplierName: (id: string | null) => string;
}

interface DragState {
  isDragging: boolean;
  taskId: string | null;
  startX: number;
  startLeft: number;
  originalStartDate: Date;
  originalEndDate: Date;
}

interface ResizeState {
  isResizing: boolean;
  taskId: string | null;
  startX: number;
  startWidth: number;
  originalStartDate: Date;
  originalEndDate: Date;
  resizeEdge: 'left' | 'right';
}

const GanttView: React.FC<GanttViewProps> = ({
  tasks,
  onEditTask,
  onDeleteTask,
  onUpdateTaskDates,
  getSupplierName,
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const todayMarkerRef = useRef<HTMLDivElement>(null);
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = normal, 2 = zoomed in, 0.5 = zoomed out
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    taskId: null,
    startX: 0,
    startLeft: 0,
    originalStartDate: new Date(),
    originalEndDate: new Date(),
  });
  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    taskId: null,
    startX: 0,
    startWidth: 0,
    originalStartDate: new Date(),
    originalEndDate: new Date(),
    resizeEdge: 'right',
  });
  const [clickStartTime, setClickStartTime] = useState<number>(0);
  const [clickStartX, setClickStartX] = useState<number>(0);

  // Filter tasks with start and end dates
  const tasksWithDates = useMemo(() => {
    return tasks.filter(task => task.start_date && task.end_date);
  }, [tasks]);

  // Find date range for all tasks
  const { startDate, endDate, totalDays } = useMemo(() => {
    const today = new Date();
    
    if (tasksWithDates.length === 0) {
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 15);
      
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 15);
      
      return { startDate, endDate, totalDays: 30 };
    }

    let minDate = new Date(tasksWithDates[0].start_date!);
    let maxDate = new Date(tasksWithDates[0].end_date!);

    tasksWithDates.forEach(task => {
      const taskStartDate = new Date(task.start_date!);
      const taskEndDate = new Date(task.end_date!);

      if (taskStartDate < minDate) minDate = taskStartDate;
      if (taskEndDate > maxDate) maxDate = taskEndDate;
    });

    // Ensure today is visible
    if (today < minDate) {
      minDate = new Date(today);
      minDate.setDate(today.getDate() - 5);
    } else if (today > maxDate) {
      maxDate = new Date(today);
      maxDate.setDate(today.getDate() + 5);
    }
    
    // Add buffer days
    minDate.setDate(minDate.getDate() - 5);
    maxDate.setDate(maxDate.getDate() + 5);

    const diffTime = Math.abs(maxDate.getTime() - minDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return { startDate: minDate, endDate: maxDate, totalDays: diffDays };
  }, [tasksWithDates]);

  // Generate date headers with zoom support
  const dateHeaders = useMemo(() => {
    const headers = [];
    const currentDate = new Date(startDate);
    
    // Adjust date interval based on zoom level
    let interval = 1;
    if (zoomLevel >= 2) interval = 1; // Show every day
    else if (zoomLevel >= 1) interval = 1; // Show every day
    else if (zoomLevel >= 0.5) interval = 2; // Show every 2 days
    else interval = 7; // Show weekly
    
    for (let i = 0; i <= totalDays; i += interval) {
      headers.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + interval);
    }
    
    return headers;
  }, [startDate, totalDays, zoomLevel]);

  // Calculate task position and width with zoom support
  const getTaskStyle = (task: ProductionTask | SampleTask) => {
    if (!task.start_date || !task.end_date) return {};

    const taskStart = new Date(task.start_date);
    const taskEnd = new Date(task.end_date);
    
    const startDiff = Math.floor((taskStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const duration = Math.ceil((taskEnd.getTime() - taskStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    const left = (startDiff / totalDays) * 100;
    const width = (duration / totalDays) * 100;
    
    return {
      left: `${left}%`,
      width: `${width}%`,
      backgroundColor: task.status === 'pending' 
        ? '#FEF3C7' 
        : task.status === 'in_progress'
        ? '#DBEAFE' 
        : '#D1FAE5',
      borderColor: task.status === 'pending' 
        ? '#F59E0B' 
        : task.status === 'in_progress'
        ? '#3B82F6' 
        : '#10B981',
    };
  };

  // Calculate today marker position
  const getTodayMarkerPosition = useMemo(() => {
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const position = (daysDiff / totalDays) * 100;
    return `${position}%`;
  }, [startDate, totalDays]);

  // Handle mouse down for dragging
  const handleMouseDown = useCallback((e: React.MouseEvent, taskId: string, task: ProductionTask | SampleTask) => {
    if (!task.start_date || !task.end_date) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX;
    const startLeft = rect.left;
    
    // Record click start for potential click detection
    setClickStartTime(Date.now());
    setClickStartX(e.clientX);
    
    setDragState({
      isDragging: true,
      taskId,
      startX,
      startLeft,
      originalStartDate: new Date(task.start_date),
      originalEndDate: new Date(task.end_date),
    });
  }, []);

  // Handle mouse down for resizing
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, taskId: string, task: ProductionTask | SampleTask, edge: 'left' | 'right') => {
    if (!task.start_date || !task.end_date) return;
    
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX;
    const startWidth = rect.width;
    
    setResizeState({
      isResizing: true,
      taskId,
      startX,
      startWidth,
      originalStartDate: new Date(task.start_date),
      originalEndDate: new Date(task.end_date),
      resizeEdge: edge,
    });
  }, []);

  // Handle mouse move for dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (dragState.isDragging && dragState.taskId) {
        const deltaX = e.clientX - dragState.startX;
        const deltaDays = Math.round(deltaX / (timelineRef.current?.clientWidth || 1) * totalDays);
        
        const newStartDate = addDays(dragState.originalStartDate, deltaDays);
        const newEndDate = addDays(dragState.originalEndDate, deltaDays);
        
        // Update task dates in real-time (visual feedback)
        const taskElement = document.querySelector(`[data-task-id="${dragState.taskId}"]`) as HTMLElement;
        if (taskElement) {
          const newLeft = ((newStartDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) / totalDays) * 100;
          taskElement.style.left = `${newLeft}%`;
        }
      }
      
      if (resizeState.isResizing && resizeState.taskId) {
        const deltaX = e.clientX - resizeState.startX;
        const deltaDays = Math.round(deltaX / (timelineRef.current?.clientWidth || 1) * totalDays);
        
        let newStartDate = new Date(resizeState.originalStartDate);
        let newEndDate = new Date(resizeState.originalEndDate);
        
        if (resizeState.resizeEdge === 'left') {
          newStartDate = addDays(resizeState.originalStartDate, deltaDays);
        } else {
          newEndDate = addDays(resizeState.originalEndDate, deltaDays);
        }
        
        // Ensure minimum duration of 1 day
        if (differenceInDays(newEndDate, newStartDate) < 1) {
          if (resizeState.resizeEdge === 'left') {
            newStartDate = subDays(newEndDate, 1);
          } else {
            newEndDate = addDays(newStartDate, 1);
          }
        }
        
        // Update task width in real-time (visual feedback)
        const taskElement = document.querySelector(`[data-task-id="${resizeState.taskId}"]`) as HTMLElement;
        if (taskElement) {
          const duration = differenceInDays(newEndDate, newStartDate) + 1;
          const width = (duration / totalDays) * 100;
          taskElement.style.width = `${width}%`;
          
          if (resizeState.resizeEdge === 'left') {
            const newLeft = ((newStartDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) / totalDays) * 100;
            taskElement.style.left = `${newLeft}%`;
          }
        }
      }
    };

    const handleMouseUp = () => {
      if (dragState.isDragging && dragState.taskId) {
        const task = tasks.find(t => t.id === dragState.taskId);
        if (task && task.start_date && task.end_date) {
          const taskElement = document.querySelector(`[data-task-id="${dragState.taskId}"]`) as HTMLElement;
          if (taskElement) {
            const left = parseFloat(taskElement.style.left) / 100;
            const deltaDays = Math.round(left * totalDays);
            const newStartDate = addDays(startDate, deltaDays);
            const newEndDate = addDays(newStartDate, differenceInDays(new Date(task.end_date), new Date(task.start_date)));
            
            onUpdateTaskDates(dragState.taskId, newStartDate.toISOString(), newEndDate.toISOString());
          }
        }
        
        // Check if this was a click (short duration and minimal movement)
        const clickDuration = Date.now() - clickStartTime;
        const clickDistance = Math.abs(dragState.startX - clickStartX);
        
        if (clickDuration < 200 && clickDistance < 5) {
          // This was a click, open the edit modal
          const task = tasks.find(t => t.id === dragState.taskId);
          if (task) {
            onEditTask(task);
          }
        }
        
        setDragState({
          isDragging: false,
          taskId: null,
          startX: 0,
          startLeft: 0,
          originalStartDate: new Date(),
          originalEndDate: new Date(),
        });
      }
      
      if (resizeState.isResizing && resizeState.taskId) {
        const task = tasks.find(t => t.id === resizeState.taskId);
        if (task && task.start_date && task.end_date) {
          const taskElement = document.querySelector(`[data-task-id="${resizeState.taskId}"]`) as HTMLElement;
          if (taskElement) {
            const left = parseFloat(taskElement.style.left) / 100;
            const width = parseFloat(taskElement.style.width) / 100;
            
            const newStartDate = addDays(startDate, Math.round(left * totalDays));
            const newEndDate = addDays(newStartDate, Math.round(width * totalDays) - 1);
            
            onUpdateTaskDates(resizeState.taskId, newStartDate.toISOString(), newEndDate.toISOString());
          }
        }
        
        setResizeState({
          isResizing: false,
          taskId: null,
          startX: 0,
          startWidth: 0,
          originalStartDate: new Date(),
          originalEndDate: new Date(),
          resizeEdge: 'right',
        });
      }
    };

    if (dragState.isDragging || resizeState.isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, resizeState, tasks, startDate, totalDays, onUpdateTaskDates]);

  // Center timeline on today's date
  useEffect(() => {
    if (timelineRef.current && todayMarkerRef.current) {
      const timelineWidth = timelineRef.current.scrollWidth;
      const todayPosition = (parseFloat(getTodayMarkerPosition) / 100) * timelineWidth;
      const containerWidth = timelineRef.current.clientWidth;
      const scrollPosition = todayPosition - (containerWidth / 2);
      
      timelineRef.current.scrollLeft = Math.max(0, scrollPosition);
    }
  }, [getTodayMarkerPosition]);

  // Zoom functions
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev * 1.5, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev / 1.5, 0.25));
  const handleResetZoom = () => setZoomLevel(1);

  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No hay tareas registradas para esta producción
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Vista Gantt</h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              icon={<ZoomOut className="h-4 w-4" />}
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0.25}
            >
              Zoom Out
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetZoom}
            >
              {Math.round(zoomLevel * 100)}%
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<ZoomIn className="h-4 w-4" />}
              onClick={handleZoomIn}
              disabled={zoomLevel >= 3}
            >
              Zoom In
            </Button>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded bg-amber-100 border border-amber-500"></div>
            <span>Pendiente</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded bg-blue-100 border border-blue-500"></div>
            <span>En Progreso</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded bg-green-100 border border-green-500"></div>
            <span>Completada</span>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="flex bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Fixed Task List Panel */}
        <div className="w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="h-16 flex items-center p-3 font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="flex-1">Tarea</div>
            <div className="text-right">Estado</div>
          </div>

          {/* Task List */}
          {tasks.map((task) => (
            <div key={task.id} className="h-16 p-3 border-b border-gray-200 dark:border-gray-700 flex flex-col justify-center hover:bg-gray-50 dark:hover:bg-gray-700">
              <div className="font-medium text-gray-900 dark:text-white text-sm mb-1">{task.task_name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{getSupplierName(task.supplier_id)}</div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {task.start_date && task.end_date ? 
                    `${formatArgentinaDate(task.start_date, 'dd/MM')} - ${formatArgentinaDate(task.end_date, 'dd/MM')}` : 
                    'Sin fechas'
                  }
                </div>
                <div className="flex items-center space-x-1">
                  {task.status === 'pending' && <Clock className="h-3 w-3 text-amber-500" />}
                  {task.status === 'in_progress' && <Play className="h-3 w-3 text-blue-500" />}
                  {task.status === 'finished' && <CheckCircle className="h-3 w-3 text-green-500" />}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable Timeline Panel */}
        <div className="flex-1 overflow-x-auto" ref={timelineRef}>
          <div className="min-w-max">
            {/* Date Headers */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
              {dateHeaders.map((date, index) => (
                <div 
                  key={index} 
                  className="flex-1 min-w-[60px] p-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700"
                >
                  <div>{date.getDate()}</div>
                  <div className="text-xs text-gray-400">{format(date, 'MMM')}</div>
                </div>
              ))}
            </div>

            {/* Tasks Timeline */}
            {tasks.map((task) => (
              <div key={task.id} className="h-16 mb-0 relative">
                <div className="relative h-16 bg-gray-100 dark:bg-gray-800 rounded">
                  {/* Today marker */}
                  <div 
                    ref={todayMarkerRef}
                    className="absolute top-0 h-full w-0.5 bg-red-500 z-10"
                    style={{ left: getTodayMarkerPosition }}
                  ></div>
                  
                  {task.start_date && task.end_date && (
                    <div 
                      data-task-id={task.id}
                      className="absolute top-1 h-14 rounded border-2 flex items-center justify-center text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity select-none"
                      style={getTaskStyle(task)}
                      onMouseDown={(e) => handleMouseDown(e, task.id, task)}
                      title={`${task.task_name} - ${formatArgentinaDate(task.start_date)} a ${formatArgentinaDate(task.end_date)}`}
                    >
                      <span className="px-2 truncate text-center">
                        {task.task_name}
                      </span>
                      
                      {/* Left resize handle */}
                      <div 
                        className="absolute left-0 top-0 w-2 h-full cursor-ew-resize bg-transparent hover:bg-black/10"
                        onMouseDown={(e) => handleResizeMouseDown(e, task.id, task, 'left')}
                      />
                      
                      {/* Right resize handle */}
                      <div 
                        className="absolute right-0 top-0 w-2 h-full cursor-ew-resize bg-transparent hover:bg-black/10"
                        onMouseDown={(e) => handleResizeMouseDown(e, task.id, task, 'right')}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks without dates */}
      {tasks.filter(task => !task.start_date || !task.end_date).length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
            Tareas sin fechas asignadas
          </h3>
          <div className="space-y-3">
            {tasks.filter(task => !task.start_date || !task.end_date).map(task => (
              <div 
                key={task.id} 
                className={`p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                  task.status === 'pending' 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 dark:border-amber-400' 
                    : task.status === 'in_progress'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400'
                }`}
                onClick={() => onEditTask(task)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white">{task.task_name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {getSupplierName(task.supplier_id)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Costo: {formatCurrency(task.cost)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditTask(task)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteTask(task.id);
                      }}
                    >
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttView;